import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { memo, ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { ImageZoomCard } from './ImageZoomCard';

interface ImageCarouselProps {
  children: ReactNode;
  currentIndex: number;
  totalCount: number;
  onPrevious: () => void;
  onNext: () => void;
  initialZoomedScale?: number;
  hasZoom?: boolean;
}

export const ImageCarousel = memo(function ({
  children,
  currentIndex,
  totalCount,
  onPrevious,
  onNext,
  initialZoomedScale,
  hasZoom = false,
}: ImageCarouselProps): ReactElement {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < totalCount - 1;

  // Determine if carousel features should be shown
  const showCarousel = useMemo(() => {
    return totalCount > 1;
  }, [totalCount]);

  const handleOpenModal = useCallback(() => {
    if (!hasZoom) return;
    setIsModalOpen(true);
  }, [hasZoom]);

  const handleCloseModal = useCallback(() => {
    if (!hasZoom) return;
    setIsModalOpen(false);
  }, [hasZoom]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isModalOpen) return;

      switch (event.key) {
        case 'Escape':
          handleCloseModal();
          break;
        case 'ArrowLeft':
          if (currentIndex > 0) {
            onPrevious();
          }
          break;
        case 'ArrowRight':
          if (currentIndex < totalCount - 1) {
            onNext();
          }
          break;
      }
    },
    [isModalOpen, handleCloseModal, currentIndex, totalCount, onPrevious, onNext]
  );

  useEffect(() => {
    if (isModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isModalOpen, handleKeyDown]);

  return (
    <Box display='flex' flexDirection='column' width='100%' height='100%'>
      <Box
        display='flex'
        alignItems='center'
        justifyContent='center'
        flex={1}
        minHeight={0}
        overflow='hidden'
        onClick={handleOpenModal}
        sx={{
          cursor: hasZoom ? 'pointer' : 'inherit',
        }}
      >
        {children}
      </Box>

      {/* Always rendered (even for a single image) so the image area above stays the same size either way;
          hidden rather than removed when there's nothing to navigate, so it still reserves its layout space. */}
      <Box
        display='flex'
        alignItems='center'
        justifyContent='center'
        gap={1}
        paddingTop={1}
        flexShrink={0}
        sx={{ visibility: showCarousel ? 'visible' : 'hidden' }}
      >
        <IconButton onClick={onPrevious} disabled={!hasPrevious} size='small'>
          <ChevronLeft />
        </IconButton>

        <Typography variant='caption' color='text.secondary'>
          {`${currentIndex + 1} / ${totalCount}`}
        </Typography>

        <IconButton onClick={onNext} disabled={!hasNext} size='small'>
          <ChevronRight />
        </IconButton>
      </Box>

      {/* Full screen modal */}
      {hasZoom && (
        <ImageZoomCard
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          {...(showCarousel && {
            currentIndex,
            totalCount,
            onPrevious,
            onNext,
            hasPrevious,
            hasNext,
          })}
          initialScale={initialZoomedScale}
        >
          {children}
        </ImageZoomCard>
      )}
    </Box>
  );
});
